"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { AppFile } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { usePathname } from "next/navigation";

interface MediaContextType {
  selectedFiles: AppFile[];
  onFileSelect: (files: AppFile[]) => void;
  handleSave: (folder?: string) => Promise<void>;
  handleDelete: (publicIds: string[]) => Promise<void>;
  handleRefresh: () => Promise<void>;
  handleSearch: (query: string) => Promise<void>;
  handleLoadMore: () => Promise<void>;
  files: AppFile[];
  setFiles: (files: AppFile[] | ((prev: AppFile[]) => AppFile[])) => void;
  isLoading: boolean;
  isSaving: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  searchQuery: string;
  currentFolder?: string;
}

const MediaContext = createContext<MediaContextType | undefined>(undefined);

interface MediaProviderProps {
  children: React.ReactNode;
  onSave: (files: AppFile[], folder?: string) => Promise<void>;
  onDelete: (publicIds: string[]) => Promise<void>;
  onRefresh: () => Promise<void>;
  initialFiles: AppFile[];
  loadFilesForPath: (
    pathname: string,
    options?: {
      search?: string;
      nextCursor?: string;
      limit?: number;
      fileTypes?: string[];
    }
  ) => Promise<{
    files: AppFile[];
    currentFolder: string;
    hasMore: boolean;
    nextCursor: string | null;
  }>;
}

export function MediaProvider({
  children,
  onSave,
  onDelete,
  onRefresh,
  initialFiles,
  loadFilesForPath,
}: MediaProviderProps) {
  const pathname = usePathname();
  const [selectedFiles, setSelectedFiles] = useState<AppFile[]>([]);
  const [files, setFiles] = useState<AppFile[]>(initialFiles);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentFolder, setCurrentFolder] = useState<string>("default");
  const { toast } = useToast();

  const handleSave = async (folder?: string) => {
    setIsSaving(true);
    try {
      // Find files that need processing (blob URLs need conversion to base64)
      const filesToProcess = files.filter(
        (file) =>
          file.url.startsWith("blob:") ||
          file.url.startsWith("data:") ||
          !file.url.includes("cloudinary.com")
      );

      if (filesToProcess.length === 0) {
        return; // No files to save
      }

      // Process files that are already in base64 format (blob URLs should have been converted earlier)
      const processedFiles = filesToProcess.filter((file) =>
        file.url.startsWith("data:")
      );

      await onSave(processedFiles, folder);
      await loadFiles(); // Reload files after save
    } catch (error) {
      console.error("Error saving files:", error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (publicIds: string[]) => {
    try {
      await onDelete(publicIds);
      await loadFiles(); // Reload files after delete
    } catch (error) {
      console.error("Error deleting files:", error);
      throw error;
    }
  };

  const handleRefresh = async () => {
    try {
      await onRefresh();
      setNextCursor(null);
      setHasMore(false);
      await loadFiles(); // Reload files after refresh
    } catch (error) {
      console.error("Error refreshing files:", error);
      throw error;
    }
  };

  const loadFiles = useCallback(
    async (resetPagination = true) => {
      setIsLoading(true);
      try {
        const result = await loadFilesForPath(pathname, {
          search: searchQuery,
          nextCursor: resetPagination ? undefined : nextCursor || undefined,
          limit: 20,
        });

        if (resetPagination) {
          setFiles(result.files);
        } else {
          setFiles((prev) => [...prev, ...result.files]);
        }

        setCurrentFolder(result.currentFolder);
        setHasMore(result.hasMore);
        setNextCursor(result.nextCursor);
      } catch (error) {
        console.error("Error loading files:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load media files",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [loadFilesForPath, pathname, searchQuery, nextCursor, toast]
  );

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || !nextCursor) return;

    setIsLoadingMore(true);
    try {
      const result = await loadFilesForPath(pathname, {
        search: searchQuery,
        nextCursor: nextCursor,
        limit: 20,
      });

      setFiles((prev) => [...prev, ...result.files]);
      setHasMore(result.hasMore);
      setNextCursor(result.nextCursor);
    } catch (error) {
      console.error("Error loading more files:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load more files",
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    nextCursor,
    loadFilesForPath,
    pathname,
    searchQuery,
    toast,
  ]);

  const handleSearch = useCallback(
    async (query: string) => {
      console.log("🎯 MediaContext handleSearch called:", {
        query,
        queryLength: query.length,
        hasQuery: !!query && query.trim() !== "",
        pathname,
      });

      setSearchQuery(query);
      setNextCursor(null);
      setHasMore(false);
      setIsLoading(true);

      try {
        console.log(
          "📞 MediaContext calling loadFilesForPath with search:",
          query
        );
        const result = await loadFilesForPath(pathname, {
          search: query,
          nextCursor: undefined,
          limit: 20,
        });

        console.log("📥 MediaContext received search results:", {
          filesCount: result.files.length,
          hasMore: result.hasMore,
          searchUsed: query,
          actualFiles: result.files.map((f) => f.name),
        });

        console.log("🔄 MediaContext setting files to:", result.files);
        setFiles(result.files);
        setCurrentFolder(result.currentFolder);
        setHasMore(result.hasMore);
        setNextCursor(result.nextCursor);
      } catch (error) {
        console.error("Error searching files:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to search media files",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [loadFilesForPath, pathname, toast]
  );

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const value = {
    selectedFiles,
    onFileSelect: setSelectedFiles,
    handleSave,
    handleDelete,
    handleRefresh,
    handleSearch,
    handleLoadMore,
    files,
    setFiles,
    isLoading,
    isSaving,
    isLoadingMore,
    hasMore,
    searchQuery,
    currentFolder,
  };

  return (
    <MediaContext.Provider value={value}>{children}</MediaContext.Provider>
  );
}

export function useMedia() {
  const context = useContext(MediaContext);
  if (context === undefined) {
    throw new Error("useMedia must be used within a MediaProvider");
  }
  return context;
}
